import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, RefreshCw, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get stored credentials from sessionStorage
const getAuthHeader = () => {
  const creds = sessionStorage.getItem('lyca_auth');
  if (creds) {
    return { Authorization: `Basic ${creds}` };
  }
  return {};
};

export default function PlanTypes() {
  const [planTypes, setPlanTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPlanType, setSelectedPlanType] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    default_price: 0,
  });

  const fetchPlanTypes = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();
      const response = await axios.get(`${API}/plan-types`, { headers });
      setPlanTypes(response.data);
    } catch (error) {
      console.error("Error fetching plan types:", error);
      toast.error("Failed to load plan types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlanTypes();
  }, [fetchPlanTypes]);

  const handleOpenDialog = (planType = null) => {
    if (planType) {
      setSelectedPlanType(planType);
      setFormData({
        name: planType.name,
        description: planType.description || "",
        default_price: planType.default_price,
      });
    } else {
      setSelectedPlanType(null);
      setFormData({
        name: "",
        description: "",
        default_price: 0,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeader();
      if (selectedPlanType) {
        await axios.put(`${API}/plan-types/${selectedPlanType.id}`, formData, { headers });
        toast.success("Plan type updated successfully");
      } else {
        await axios.post(`${API}/plan-types`, formData, { headers });
        toast.success("Plan type created successfully");
      }
      setIsDialogOpen(false);
      fetchPlanTypes();
    } catch (error) {
      console.error("Error saving plan type:", error);
      toast.error(error.response?.data?.detail || "Failed to save plan type");
    }
  };

  const handleDelete = async () => {
    try {
      const headers = getAuthHeader();
      await axios.delete(`${API}/plan-types/${selectedPlanType.id}`, { headers });
      toast.success("Plan type deleted successfully");
      setIsDeleteDialogOpen(false);
      setSelectedPlanType(null);
      fetchPlanTypes();
    } catch (error) {
      console.error("Error deleting plan type:", error);
      toast.error(error.response?.data?.detail || "Failed to delete plan type");
    }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 text-[#00D26A] animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="plan-types-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-white" data-testid="plan-types-title">
            Plan Types
          </h1>
          <p className="text-slate-400 mt-1">Manage Lyca Mobile plan types</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="btn-primary flex items-center gap-2"
          data-testid="add-plan-type-btn"
        >
          <Plus className="w-4 h-4" />
          Add Plan Type
        </Button>
      </div>

      {/* Plan Types Grid */}
      {planTypes.length === 0 ? (
        <div className="card">
          <div className="empty-state py-12">
            <CreditCard className="w-12 h-12 text-slate-600 mb-4" />
            <p className="text-lg">No plan types yet</p>
            <p className="text-sm">Create your first plan type to get started.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plan-types-grid">
          {planTypes.map((planType) => (
            <div
              key={planType.id}
              className="card p-6 hover:border-[#00D26A]/30 transition-colors"
              data-testid={`plan-type-card-${planType.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-[#00D26A]/10 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-[#00D26A]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{planType.name}</h3>
                    <p className="text-2xl font-bold text-[#00D26A]">
                      €{planType.default_price.toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleOpenDialog(planType)}
                    className="p-2 hover:bg-slate-800 rounded transition-colors"
                    data-testid={`edit-plan-type-${planType.id}`}
                  >
                    <Pencil className="w-4 h-4 text-[#0066FF]" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedPlanType(planType);
                      setIsDeleteDialogOpen(true);
                    }}
                    className="p-2 hover:bg-slate-800 rounded transition-colors"
                    data-testid={`delete-plan-type-${planType.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
              {planType.description && (
                <p className="text-sm text-slate-400 mt-2">{planType.description}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="plan-type-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedPlanType ? "Edit Plan Type" : "Add New Plan Type"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-slate-300">Plan Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input mt-1"
                placeholder="e.g., Basic Plan, Premium Plan"
                required
                data-testid="input-plan-name"
              />
            </div>
            <div>
              <Label htmlFor="price" className="text-slate-300">Default Price (€)</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.default_price}
                onChange={(e) => setFormData({ ...formData, default_price: parseFloat(e.target.value) || 0 })}
                className="form-input mt-1"
                data-testid="input-plan-price"
              />
            </div>
            <div>
              <Label htmlFor="description" className="text-slate-300">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="form-input mt-1"
                rows={3}
                placeholder="Plan details, inclusions, etc."
                data-testid="input-plan-description"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsDialogOpen(false)}
                className="btn-ghost"
                data-testid="cancel-plan-btn"
              >
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-plan-btn">
                {selectedPlanType ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-800" data-testid="delete-plan-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Plan Type</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure you want to delete &quot;{selectedPlanType?.name}&quot;? This action cannot be undone. Note: You cannot delete a plan type if customers are using it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-ghost" data-testid="cancel-delete-plan-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="btn-danger" data-testid="confirm-delete-plan-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
